import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button/Button';
import NumberField from 'components/NumberField/NumberField';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { formatTextTitle } from 'utils/formatText';

import type IRevenueFilter from './interfaces/IRevenueFilter';

import styles from './scss/RevenueFilter.module.scss';

function RevenueFilter({
  revenueData,
  selectedCurrentFilters,
  onApplyRevenueFilter,
  onResetRevenueFilter,
}: IRevenueFilter) {
  const { t } = useTranslation();
  const [minValue, setMinValue] = useState<number>(0);
  const [maxValue, setMaxValue] = useState<number>(Number(revenueData?.max) || 0);

  useEffect(() => {
    const revenueFilter = selectedCurrentFilters.find((filter) => filter.type === revenueData?.type);
    if (revenueFilter) {
      setMinValue(Number(revenueFilter.min) || 0);
      setMaxValue(Number(revenueFilter.max) || Number(revenueData?.max) || 0);
    }
  }, [selectedCurrentFilters, revenueData]);

  const handleMinChange = (values: { floatValue?: number }) => {
    const value = values.floatValue ?? 0;
    setMinValue(value);
  };

  const handleMaxChange = (values: { floatValue?: number }) => {
    const value = values.floatValue ?? 0;
    setMaxValue(value);
  };

  const handleApply = () => {
    onApplyRevenueFilter(minValue, maxValue);
  };

  const handleReset = () => {
    setMinValue(0);
    setMaxValue(Number(revenueData?.max) || 0);
    onResetRevenueFilter();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>{formatTextTitle(revenueData?.type || t('common.revenue'))}</h3>
        <Button
          title={t('common.reset')}
          variant={BUTTON_VARIANT.ghost}
          onClick={handleReset}
          className={styles.resetButton}
        />
      </div>
      <div className={styles.inputsWrapper}>
        <NumberField
          name="minRevenue"
          value={String(minValue)}
          onChange={handleMinChange}
          placeholder="0"
          allowNegative={false}
          max={maxValue}
          className={styles.input}
          label={t('common.min')}
        />
        <span className={styles.separator}>-</span>
        <NumberField
          name="maxRevenue"
          value={String(maxValue)}
          onChange={handleMaxChange}
          placeholder="0"
          allowNegative={false}
          max={Number(revenueData?.max) || undefined}
          className={styles.input}
          label={t('common.max')}
        />
      </div>
      <Button
        title={t('common.apply')}
        variant={BUTTON_VARIANT.primary}
        onClick={handleApply}
        className={styles.applyButton}
      />
    </div>
  );
}

export default RevenueFilter;
